import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { forwardRef, useMemo } from 'react';

import { Job } from '~hooks/useJob/models';

import JobSummaryCardStat from './JobSummaryCardStat';
import JobSummaryEditForm, { JobSummaryEditFormHandler } from './JobSummaryEditForm';

const NO_CONTENT = '-';

interface JobSummaryCardProps {
  isEditing?: boolean;
  isSubmittingForm?: boolean;
  job: Job;
  sx?: SxProps;
}

const JobSummaryCard = forwardRef<JobSummaryEditFormHandler, JobSummaryCardProps>(
  function JobSummaryCard({ isEditing, isSubmittingForm, job, sx }, ref) {
    const stats = useMemo(() => {
      const {
        adjustedLoggingEndedAt,
        adjustedLoggingStartedAt,
        approvedWorkTimeMinutes,
        breakTimeMinutes,
        firstGeofenceEntryAt,
        lastGeofenceExitAt,
        loggingEndedAt,
        loggingStartedAt,
        workTimeMinutes,
      } = job.jobSummary ?? {};

      const scheduledStart = {
        startTime: job.jobStartAt
          ? dayjs.tz(job.jobStartAt).format('hh:mm A')
          : NO_CONTENT,
        startDate: job.jobStartAt
          ? dayjs.tz(job.jobStartAt).format('ddd DD-MMM-YYYY')
          : NO_CONTENT,
      };

      const effectiveLoggingStartedAt = adjustedLoggingStartedAt ?? loggingStartedAt;
      const start = {
        value: effectiveLoggingStartedAt,
        startedAtOutput: effectiveLoggingStartedAt
          ? dayjs.tz(effectiveLoggingStartedAt).format('hh:mm A')
          : NO_CONTENT,
        geofenceEntryAtOutput: firstGeofenceEntryAt
          ? dayjs.tz(firstGeofenceEntryAt).format('hh:mm A')
          : NO_CONTENT,
      };

      const effectiveLoggingEndedAt = adjustedLoggingEndedAt ?? loggingEndedAt;
      const end = {
        value: effectiveLoggingEndedAt,
        endedAtOutput: effectiveLoggingEndedAt
          ? dayjs.tz(effectiveLoggingEndedAt).format('hh:mm A')
          : NO_CONTENT,
        geofenceExitAtOutput: lastGeofenceExitAt
          ? dayjs.tz(lastGeofenceExitAt).format('hh:mm A')
          : NO_CONTENT,
      };

      const effectiveWorkTime = approvedWorkTimeMinutes ?? workTimeMinutes;
      const totalHrs = {
        value: effectiveWorkTime,
        totalHrsOutput: isNil(effectiveWorkTime)
          ? NO_CONTENT
          : `${((effectiveWorkTime ?? 0) / 60).toFixed(2)}h`,
        breaksOutput: isNil(breakTimeMinutes) ? NO_CONTENT : `${breakTimeMinutes}min`,
      };

      return { start, end, totalHrs, scheduledStart };
    }, [job.jobSummary, job.jobStartAt]);

    const canEditJobSummary = Boolean(job.jobSummary?.editable);

    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, p: 1.5, ...sx }}
      >
        <JobSummaryCardStat
          title={t('approvals.approve_jobs.grid_expanded_block.job_tab.scheduled_start')}
          primaryText={stats.scheduledStart.startTime}
          secondaryTexts={[stats.scheduledStart.startDate]}
          sx={{ flex: 1 }}
        />

        {canEditJobSummary && isEditing ? (
          <JobSummaryEditForm
            ref={ref}
            approvedWorkTimeMinutes={stats.totalHrs.value ?? null}
            approvedWorkTimeMinutesText={`${t('approvals.driver_day.breaks')}: ${stats.totalHrs.breaksOutput}`}
            isSubmittingForm={isSubmittingForm}
            loggingEndedAt={stats.end.value ? stats.end.value : null}
            loggingEndedAtText={`${t('approvals.driver_day.last_geofence_entry')}: ${stats.end.geofenceExitAtOutput}`}
            loggingStartedAt={stats.start.value ? stats.start.value : null}
            loggingStartedAtText={`${t('approvals.driver_day.first_geofence_entry')}: ${stats.start.geofenceEntryAtOutput}`}
          />
        ) : (
          <>
            <JobSummaryCardStat
              title={t('approvals.approve_jobs.grid_expanded_block.job_tab.job_start')}
              primaryText={stats.start.startedAtOutput}
              secondaryTexts={[
                `${t('approvals.driver_day.first_geofence_entry')}: ${
                  stats.start.geofenceEntryAtOutput
                }`,
              ]}
              sx={{ flex: 1 }}
            />

            <JobSummaryCardStat
              title={t('approvals.approve_jobs.grid_expanded_block.job_tab.job_end')}
              primaryText={stats.end.endedAtOutput}
              secondaryTexts={[
                `${t('approvals.driver_day.last_geofence_entry')}: ${stats.end.geofenceExitAtOutput}`,
              ]}
              sx={{ flex: 1 }}
            />

            <JobSummaryCardStat
              isEmphasized
              title={t('approvals.driver_day.total_hrs')}
              primaryText={stats.totalHrs.totalHrsOutput}
              secondaryTexts={[
                `${t('approvals.driver_day.breaks')}: ${stats.totalHrs.breaksOutput}`,
              ]}
              sx={{ flex: 1 }}
            />
          </>
        )}
      </Box>
    );
  },
);

export default JobSummaryCard;
